import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { complianceDocumentKindLabel } from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import { Branch } from '@texas/api/endpoints/branchesApi';
import { CustomerComplianceDocument } from '@texas/api/endpoints/customersApi';
import { ComplianceDocumentDefinition } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import {
  entityWorkspaceStyles,
  getEntityWorkspaceListItemStyle,
} from '@texas/resources/theme/styles/entityWorkspace';
import { ReactSelectOption } from '@texas/types';
import { useAssignCustomerComplianceDocument } from './useAssignCustomerComplianceDocument';

interface Props {
  customerId: number;
  customerName: string;
  branches: Branch[];
  documents: CustomerComplianceDocument[];
  onClose: () => void;
}

export function AssignCustomerComplianceDocument({
  customerId,
  customerName,
  branches,
  documents,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const state = useAssignCustomerComplianceDocument(
    customerId,
    documents,
    onClose,
  );
  const branchOptions = useMemo<ReactSelectOption[]>(
    () =>
      branches.map((branch) => ({
        label: `${branch.name} (${branch.identifier})`,
        value: branch.id,
      })),
    [branches],
  );
  const close = () => {
    if (!state.loading) onClose();
  };

  return (
    <Modal
      variant="entityDirectory"
      size="xl"
      isOpen={true}
      onClose={close}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('customerComplianceDocuments.assignFromConfigs')}
        </ModalHeader>
        <ModalCloseButton isDisabled={state.loading} />
        <ModalBody>
          <Stack spacing={4}>
            <Text variant="entity-directory-description">
              {t('customerComplianceDocuments.choose', { name: customerName })}
            </Text>
            <SearchFilterInput
              style="entityDirectory"
              debounceDelay={0}
              value={state.searchTerm}
              onChange={state.setSearchTerm}
              placeholder={t('customerComplianceDocuments.search')}
            />
            <ErrorDetails error={state.definitionsError} />
            <ErrorDetails error={state.error} />
            {state.definitionsLoading ? (
              <SpinnerWithLabel label={t('general.loadingData')} />
            ) : (
              !state.definitionsError &&
              (state.definitions.length === 0 ? (
                <EntityDirectoryEmptyState>
                  {t('customerComplianceDocuments.noDefinitions')}
                </EntityDirectoryEmptyState>
              ) : (
                <Flex sx={entityWorkspaceStyles.branchItems}>
                  {state.definitions.map((definition) => (
                    <DefinitionOption
                      key={definition.id}
                      definition={definition}
                      assigned={state.assignedIds.has(definition.id)}
                      selected={state.selectedId === definition.id}
                      loading={state.loading}
                      onSelect={() => state.setSelectedId(definition.id)}
                    />
                  ))}
                </Flex>
              ))
            )}
            <Stack spacing={2}>
              <Text variant="configuration-meta">
                {t('customerComplianceDocuments.branchesOptional')}
              </Text>
              <TexasSelect<ReactSelectOption, true>
                closeMenuOnSelect={false}
                isClearable={true}
                isMulti={true}
                options={branchOptions}
                placeholder={t('general.select')}
                usePortal={true}
                value={branchOptions.filter((option) =>
                  state.selectedBranchIds.includes(option.value),
                )}
                onChange={(options) =>
                  state.setSelectedBranchIds(
                    options.map((option) => option.value),
                  )
                }
              />
              <Text variant="entity-directory-description">
                {t('customerComplianceDocuments.branchesHelp')}
              </Text>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter sx={entityWorkspaceStyles.modalFooter}>
          <Button
            variant="entityDirectorySecondary"
            isDisabled={state.loading}
            onClick={close}
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="entityDirectoryPrimary"
            isDisabled={!state.canAssign}
            isLoading={state.loading}
            onClick={state.assign}
          >
            {t('suppliers.workspace.assign')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DefinitionOption({
  definition,
  assigned,
  selected,
  loading,
  onSelect,
}: {
  definition: ComplianceDocumentDefinition;
  assigned: boolean;
  selected: boolean;
  loading: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      as="button"
      type="button"
      sx={getEntityWorkspaceListItemStyle(!assigned, selected)}
      aria-pressed={selected}
      disabled={assigned || loading}
      onClick={onSelect}
    >
      <Text sx={entityWorkspaceStyles.branchItemName}>{definition.name}</Text>
      <Text sx={entityWorkspaceStyles.branchItemNumber}>
        {definition.description}
      </Text>
      <Text sx={entityWorkspaceStyles.branchItemNumber}>
        {assigned
          ? t('article.management.alreadyAssigned')
          : complianceDocumentKindLabel(definition.kind, t)}
      </Text>
    </Box>
  );
}
